import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDataLayerStatus } from '@/app/DatabaseBootstrap'
import { BackupStatusService, type BackupStatus } from '@/db/services/backupStatusService'
import { inspectStorage, type StorageStatus } from '@/db/services/storageService'
import { paths } from '@/routes/paths'

type BackupIndicatorState =
  { status: 'loading' } | { status: 'ready'; data: BackupStatus } | { status: 'error' }

export function StoragePersistenceIndicator() {
  const { storage } = useDataLayerStatus()
  const [current, setCurrent] = useState<StorageStatus>(storage)
  const refresh = useCallback(() => {
    let active = true
    void inspectStorage(undefined, false).then((status) => {
      if (active) setCurrent(status)
    })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let cancel = refresh()
    const handleDataChange = () => {
      cancel()
      cancel = refresh()
    }
    window.addEventListener('real-machines:data-changed', handleDataChange)
    return () => {
      cancel()
      window.removeEventListener('real-machines:data-changed', handleDataChange)
    }
  }, [refresh])

  const persisted = current.supported && current.persisted
  const tone = current.critical ? 'critical' : current.warning || !persisted ? 'attention' : 'good'
  const label = current.critical
    ? `Armazenamento ${current.percentage.toFixed(0)}%`
    : current.warning
      ? `Espaço ${current.percentage.toFixed(0)}%`
      : persisted
        ? 'Banco persistente'
        : 'Persistência pendente'
  const title = current.critical
    ? 'Uso crítico de armazenamento. Faça backup e libere espaço.'
    : current.warning
      ? 'O armazenamento local ultrapassou 70% da quota estimada.'
      : persisted
        ? 'O navegador concedeu armazenamento persistente.'
        : 'O navegador ainda não garantiu armazenamento persistente.'

  return (
    <span className={`app-status app-status--${tone}`} role="status" title={title}>
      <span aria-hidden="true">●</span>
      {label}
    </span>
  )
}

export function BackupStatusIndicator() {
  const [state, setState] = useState<BackupIndicatorState>({ status: 'loading' })
  const refresh = useCallback(() => {
    let active = true
    void new BackupStatusService()
      .getStatus()
      .then((data) => {
        if (active) setState({ status: 'ready', data })
      })
      .catch(() => {
        if (active) setState({ status: 'error' })
      })
    return () => {
      active = false
    }
  }, [])

  useEffect(() => {
    let cancel = refresh()
    const handleDataChange = () => {
      cancel()
      cancel = refresh()
    }
    window.addEventListener('real-machines:data-changed', handleDataChange)
    return () => {
      cancel()
      window.removeEventListener('real-machines:data-changed', handleDataChange)
    }
  }, [refresh])

  if (state.status === 'loading')
    return <span className="app-status app-status--neutral">Verificando backup</span>
  if (state.status === 'error')
    return <span className="app-status app-status--neutral">Backup indisponível</span>
  return (
    <span
      className={`app-status app-status--${state.data.pending ? 'attention' : 'good'}`}
      role="status"
    >
      <span aria-hidden="true">●</span>
      {state.data.pending ? 'Backup pendente' : 'Backup em dia'}
    </span>
  )
}

const reminderLabels = {
  never: 'nenhum backup foi registrado',
  older_than_7_days: 'o último backup tem mais de 7 dias',
  ten_completed: 'há 10 ou mais conclusões desde o último backup',
  storage_70: 'o armazenamento local ultrapassou 70%',
} as const

export function BackupReminderBanner() {
  const service = useMemo(() => new BackupStatusService(), [])
  const [state, setState] = useState<BackupIndicatorState>({ status: 'loading' })

  useEffect(() => {
    let active = true
    const refresh = () => {
      void service
        .getStatus()
        .then((data) => {
          if (active) setState({ status: 'ready', data })
        })
        .catch(() => {
          if (active) setState({ status: 'error' })
        })
    }
    refresh()
    window.addEventListener('real-machines:data-changed', refresh)
    return () => {
      active = false
      window.removeEventListener('real-machines:data-changed', refresh)
    }
  }, [service])

  if (state.status !== 'ready' || !state.data.shouldRemind) return null

  return (
    <aside className="backup-reminder" aria-labelledby="backup-reminder-title">
      <div>
        <strong id="backup-reminder-title">Proteja os dados deste tablet</strong>
        <span>{state.data.reasons.map((reason) => reminderLabels[reason]).join(' · ')}</span>
      </div>
      <div className="button-row">
        <Link className="button button--primary" to={paths.backup}>
          Fazer backup
        </Link>
        <button
          className="button button--secondary"
          type="button"
          onClick={() => {
            service.dismissReminder()
            setState({ status: 'ready', data: { ...state.data, shouldRemind: false } })
          }}
        >
          Lembrar amanhã
        </button>
      </div>
    </aside>
  )
}
